/*
  <Avatar
    to=""             // Username to redirect to profile or live stream
    src=""            // Image Source
    alt=""            // Image Alt
    size=""           // Default value, 40px * 40px
    isLive            // Add live badge
    isVerified        // add verified icon next to the name
    followers=""      // Followers Number
    name=""           // Name
  />
*/
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
// import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { handleBigNumbers } from "../utils";

// Import Icons
import { ReactComponent as VerifiedIcon } from "../img/icons/verified.svg";

const Avatar = (props) => {
  return (
    <AvatarStyle>
      <div>
        <Image to={"/profile/" + (props.name || props.to)} size={props.size || "32px"}>
          <img
            src={props.src}
            alt={props.alt}
            width={props.size || "32px"}
            height={props.size || "32px"}
          />
        </Image>
        {props.isLive && <LiveBadge to={"/stream/" + (props.name || props.to)}>live</LiveBadge>}
      </div>
      {props.name && (
        <h6>
          {props.name}
          {props.isVerified && <VerifiedIcon />}
        </h6>
      )}
      {props.followers >= 0 && (
        <Followers onClick={props.onFollowersClick} clickAble={props.clickAble}>
          {handleBigNumbers(props.followers)} followers
        </Followers>
      )}
    </AvatarStyle>
  );
};
export default Avatar;

// Styling
const AvatarStyle = styled.div`
  text-align: center;
  position: relative;
  display: inline-block;

  > div:nth-child(1) {
    cursor: pointer;
    position: relative;
  }
  h6 {
    color: ${({ theme }) => theme.color.darkerText};
    display: inline-block;
    white-space: nowrap;
    position: relative;
    margin-top: 10px;
    svg {
      position: absolute;
      top: 44%;
      right: -15px;
      transform: translateY(-50%);
    }
  }
`;
const Image = styled(Link)`
  background-color: ${({ theme }) => theme.color.secondaryBg};
  border-radius: 1000px;
  display: inline-block;
  position: relative;
  user-select: none;
  width: ${({ size }) => `clamp(${size}, ${size}, ${size})`};
  height: ${({ size }) => `clamp(${size}, ${size}, ${size})`};

  ::before {
    content: "";
    z-index: -1;
    width: calc(100% + 4px);
    height: calc(100% + 4px);
    position: absolute;
    left: -2px;
    top: -2px;
    transition: transform 300ms ease-in-out;
    border-radius: 1000px;
    background: rgb(154, 52, 255);
    background: linear-gradient(90deg, rgba(154, 52, 255, 1) 34%, rgba(255, 0, 168, 1) 100%);
  }

  img {
    background-color: ${({ theme }) => theme.color.secondaryBg};
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1000px;
    overflow: hidden;
    border: 2px solid ${({ theme }) => theme.color.background};
  }

  :hover {
    ::before {
      animation: 300ms slidein;
    }
  }
  @keyframes slidein {
    from {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    to {
      transform: scale(1);
    }
  }
`;
const Followers = styled.span`
  color: ${({ theme }) => theme.color.lighterText};
  font-size: 12px;
  line-height: 100%;
  margin-top: 10px;
  display: block;
  ${({ clickAble, theme }) =>
    clickAble &&
    `
    cursor: pointer;
    transition: color 100ms;
    :hover{
      transition: color 200ms;
      color: ${theme.color.primary};
    }
  `}
`;
const LiveBadge = styled(Link)`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: inline-block;
  border-radius: 3px;
  width: 40%;
  height: 13%;
  min-width: 42px;
  min-height: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: linear-gradient(120.54deg, #9a34ff -27.83%, #ff00a8 31.19%);
  text-align: center;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 70%;
  font-family: "Poppins Medium";
  letter-spacing: 1px;
  line-height: 1;
  padding-top: 2px;
  padding-bottom: 1px;
`;

const SmallAvatarStyle = styled.div`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  div {
    border: 2px solid red;
  }
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.border};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  :focus {
    box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.7);
  }
`;

const InitialsStyle = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${({ size }) => `calc(${size} * 0.4)`};
  font-weight: bold;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.color.background};
`;

export const SmallAvatar = ({ to, src, alt, name, size = "32px", ...rest }) => {
  // Fonction pour obtenir les 2 premières lettres du nom
  const getInitials = (text) => {
    if (!text) return "U";
    return text.substring(0, 2).toUpperCase();
  };

  // Fonction pour générer une couleur basée sur le nom
  const getColorFromName = (text) => {
    if (!text) return "#9a34ff";
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      "#9a34ff", "#ff00a8", "#ff6b35", "#4ecdc4", "#45b7d1",
      "#96ceb4", "#feca57", "#ff9ff3", "#54a0ff", "#5f27cd"
    ];
    return colors[Math.abs(hash) % colors.length];
  };

  const initials = getInitials(name || alt);
  const bgColor = getColorFromName(name || alt);

  if (to)
    return (
      <Link style={{ borderRadius: "999px" }} to={to} {...rest}>
        <SmallAvatarStyle size={size}>
          {src ? (
            <img src={src} alt={alt} />
          ) : (
            <InitialsStyle size={size} bgColor={bgColor}>
              {initials}
            </InitialsStyle>
          )}
        </SmallAvatarStyle>
      </Link>
    );
  return (
    <SmallAvatarStyle size={size} {...rest}>
      {src ? (
        <img src={src} alt={alt} />
      ) : (
        <InitialsStyle size={size} bgColor={bgColor}>
          {initials}
        </InitialsStyle>
      )}
    </SmallAvatarStyle>
  );
};
